import {Fragment, memo, ReactNode} from "react";
import {NodeIcon, NodeLabel, ConnectionAnchor} from "./index";
import {ConnectionLine, ConnectionArrowHead} from "./ConnectionArrow";
import {ConnectionLabel} from "./ConnectionLabel";
import {BackgroundGrid} from "./BackgroundGrid";
import {Viewport} from "./Viewport";
import {Swimlane} from "./Swimlane";
import {DEMO_SWIMLANES} from "./swimlaneConfigs";
import {ClientDiagram} from "../../utility/types";
import {Camera} from "../../utility/camera";
import type {SwimlaneConfig} from "../../index";
import type {MockSourceEvidence} from "../../../../types";

interface DiagramContentProps {
  diagram: ClientDiagram;
  camera: Camera;
  showBackgroundGrid?: boolean;
  showSwimlanes?: boolean;
  swimlanes?: SwimlaneConfig[];
  extraElements?: ReactNode;
  nodeCitations?: Record<string, number[]>;
  evidence?: MockSourceEvidence[];
}

export const DiagramContent = memo(
  ({
    diagram,
    camera,
    showBackgroundGrid = true,
    showSwimlanes = false,
    swimlanes,
    extraElements,
    nodeCitations,
    evidence,
  }: DiagramContentProps) => {
    // Use provided swimlanes or fall back to DEMO_SWIMLANES
    const swimlanesToRender = swimlanes ?? DEMO_SWIMLANES;

    return (
      <>
        {showBackgroundGrid && <BackgroundGrid camera={camera} />}
        <Viewport camera={camera}>
          {/* Swimlanes - render first (behind everything) */}
          {showSwimlanes &&
            swimlanesToRender.map((lane, i) => (
              <Swimlane
                key={i}
                label={lane.label}
                color={lane.color}
                minX={lane.minX}
                maxX={lane.maxX}
                camera={camera}
              />
            ))}

          {extraElements}

          {/* Connection lines */}
          {[...diagram.connections.values()].map(connection => (
            <ConnectionLine key={`${connection.id}-line`} connection={connection} nodes={diagram.nodes} />
          ))}

          {/* Connection anchors */}
          {[...diagram.connections.values()].map(connection =>
            connection.anchors.map((anchor, index) => (
              <ConnectionAnchor key={`${connection.id}-anchor-${index}`} position={anchor.position} />
            )),
          )}

          {/* Connection arrow heads */}
          {[...diagram.connections.values()].map(connection => (
            <ConnectionArrowHead key={`${connection.id}-arrow`} connection={connection} nodes={diagram.nodes} />
          ))}

          {/* Connection labels */}
          {[...diagram.connections.values()].map(connection => (
            <ConnectionLabel key={connection.id} connection={connection} nodes={diagram.nodes} />
          ))}

          {/* Nodes and labels */}
          {[...diagram.nodes.values()].map(node => {
            const citations = nodeCitations?.[node.id];
            return (
              <Fragment key={node.id}>
                <NodeIcon node={node} />
                <NodeLabel node={node} citations={citations} evidence={evidence} />
              </Fragment>
            );
          })}
        </Viewport>
      </>
    );
  },
);
